/**
 * Join consultant registration — component. Submits `[data-join-consultant-form]` to JSON API.
 * POST /apps/public/consultants (Shopify app proxy). Level: assets/join-consultant-config.json.
 * Optional: window.joinConsultantRegisterStrings from Liquid for translated messages.
 * On successful POST /apps/public/consultants, `consultantCode` from the response is written to `#consultant-consultanatId`.
 * When membership is added to cart after registration, navigates via permalink:
 * `/cart/clear?return_to=/cart/add?...&return_to=/checkout` (cart cleared, membership only, then checkout).
 * Cart attribute `consultantId` is set on the add step from `#consultant-consultanatId` (API code applied first).
 *
 * @class JoinConsultantRegisterFormComponent
 * @class JoinConsultantRegisterFormBinding
 */
const JOIN_CONSULTANT_DEFAULT_API_URL = '/apps/public/consultants';

class JoinConsultantRegisterFormComponent {
  static _resolvedConfig = null;

  /**
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  static str(key, fallback) {
    const S = window.joinConsultantRegisterStrings || {};
    return S[key] || fallback;
  }

  /**
   * @param {string} id
   * @returns {HTMLElement|null}
   */
  static byId(id) {
    return document.getElementById(id);
  }

  static loadConfigFromThemeJson() {
    const C = JoinConsultantRegisterFormComponent;
    const url = window.__joinConsultantConfigUrl;
    if (!url) {
      C._resolvedConfig = { apiUrl: JOIN_CONSULTANT_DEFAULT_API_URL, level: 1 };
      return Promise.resolve();
    }
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('config fetch');
        return r.json();
      })
      .then(function (j) {
        C._resolvedConfig = {
          apiUrl: (j && j.apiUrl && String(j.apiUrl).trim()) || JOIN_CONSULTANT_DEFAULT_API_URL,
          level: parseInt(j && j.level, 10) || 1,
        };
      })
      .catch(function () {
        C._resolvedConfig = { apiUrl: JOIN_CONSULTANT_DEFAULT_API_URL, level: 1 };
      });
  }

  static cfg() {
    const C = JoinConsultantRegisterFormComponent;
    return (
      C._resolvedConfig || {
        apiUrl: JOIN_CONSULTANT_DEFAULT_API_URL,
        level: 1,
      }
    );
  }

  static consultantIdFromEmail(email) {
    let local = String(email || '').split('@')[0] || '';
    let id = local.replace(/[^a-zA-Z0-9]/g, '');
    if (id.length < 2) id = 'consultant' + Math.random().toString(36).slice(2, 8);
    return id.slice(0, 48);
  }

  static digitsPhone(val) {
    return String(val || '').replace(/\D/g, '');
  }

  /**
   * @param {string} val
   * @returns {string}
   */
  static formatConsultantPhoneInput(val) {
    const digits = String(val || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    return digits.slice(0, 3) + ' ' + digits.slice(3);
  }

  /**
   * @param {string} val — expected format: 202 4561437
   * @returns {boolean}
   */
  static isValidConsultantPhone(val) {
    return /^\d{3}\s\d{7}$/.test(String(val || '').trim());
  }

  static initConsultantPhoneField() {
    const C = JoinConsultantRegisterFormComponent;
    const panel = document.querySelector('[data-consultant-form]');
    if (!panel) return;
    const phone = panel.querySelector('#consultant-phone');
    if (!phone || phone.dataset.watkinsPhoneFormatBound === 'true') return;
    phone.dataset.watkinsPhoneFormatBound = 'true';

    const invalidMsg = C.str(
      'phoneInvalid',
      'Enter phone as 202 4561437 (area code, space, then 7 digits).'
    );
    phone.setAttribute('title', invalidMsg);
    phone.setAttribute('maxlength', '11');

    phone.addEventListener('input', function () {
      const formatted = C.formatConsultantPhoneInput(phone.value);
      if (formatted !== phone.value) {
        phone.value = formatted;
      }
      if (!phone.value.trim() || C.isValidConsultantPhone(phone.value)) {
        phone.setCustomValidity('');
      }
    });

    phone.addEventListener('blur', function () {
      if (!phone.value.trim()) {
        phone.setCustomValidity('');
        return;
      }
      phone.setCustomValidity(C.isValidConsultantPhone(phone.value) ? '' : invalidMsg);
    });
  }

  static isJoinConsultantPrefillLocked() {
    const $ = JoinConsultantRegisterFormComponent.byId;
    const toggle = $('customer-accounts-has-consultant');
    const shell = $('customer-accounts-consultant-shell');
    const sponsorSection = shell && shell.closest('.customer-accounts-sponsor-consultant');
    const sponsorRow = toggle && toggle.closest('.form-check');
    return (
      sponsorRow &&
      sponsorRow.hasAttribute('hidden') &&
      sponsorSection &&
      sponsorSection.classList.contains('customer-accounts-sponsor-consultant--consultant-prefilled')
    );
  }

  static syncConsultantSponsorShell() {
    const $ = JoinConsultantRegisterFormComponent.byId;
    const toggle = $('customer-accounts-has-consultant');
    const shell = $('customer-accounts-consultant-shell');
    const consultant = $('consultant-consultanatId');
    const hidden = $('customer-accounts-has-consultant-hidden');
    if (!toggle || !consultant) return;

    if (JoinConsultantRegisterFormComponent.isJoinConsultantPrefillLocked()) {
      toggle.setAttribute('aria-expanded', 'true');
      consultant.disabled = false;
      consultant.required = true;
      if (shell) shell.classList.remove('customer-accounts-consultant-shell--locked');
      if (hidden && hidden.value !== 'Yes') hidden.value = 'Yes';
      return;
    }

    const on = toggle.checked;
    toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    consultant.disabled = !on;
    consultant.required = !!on;
    if (shell) shell.classList.toggle('customer-accounts-consultant-shell--locked', !on);
    if (!on) {
      consultant.value = '';
      consultant.setCustomValidity('');
      if (typeof CustomerAccountsForm !== 'undefined' && CustomerAccountsForm.resetConsultantLockHint) {
        CustomerAccountsForm.resetConsultantLockHint('consultant-consultanatId-lock-hint');
      }
    }
    if (hidden) hidden.value = on ? 'Yes' : 'No';
  }

  /**
   * Active market for join-consultant: CA when Watkins country is CA, otherwise US.
   * @returns {'US' | 'CA'}
   */
  static getJoinConsultantMarketIso() {
    if (typeof globalThis.getActiveWatkinsCountryForDetectedContent === 'function') {
      try {
        const active = globalThis.getActiveWatkinsCountryForDetectedContent();
        if (active === 'CA' || active === 'US') return active;
      } catch {
        /* */
      }
    }
    try {
      const wc = String(globalThis.localStorage.getItem('watkins_country') || '')
        .trim()
        .toUpperCase();
      if (wc === 'CA' || wc === 'CAN' || wc === 'CANADA') return 'CA';
    } catch {
      /* */
    }
    const country = JoinConsultantRegisterFormComponent.byId('consultant-country');
    if (country && country.value) {
      const v = String(country.value).trim().toUpperCase();
      if (v === 'CA' || v === 'CAN' || v === 'CANADA' || v.includes('CANADA')) return 'CA';
    }
    return 'US';
  }

  /**
   * Tax ID / SSN is required for US only; optional for CA.
   */
  static syncTaxIdRequiredByMarket() {
    const taxId = JoinConsultantRegisterFormComponent.byId('consultant-taxId');
    if (!taxId) return;

    const isCa = JoinConsultantRegisterFormComponent.getJoinConsultantMarketIso() === 'CA';
    taxId.required = !isCa;
    taxId.setAttribute('aria-required', isCa ? 'false' : 'true');
    if (isCa) {
      taxId.setCustomValidity('');
      taxId.classList.remove('is-invalid');
      taxId.removeAttribute('aria-invalid');
    }

    const mark = document.querySelector('[data-join-consultant-tax-id-required-mark]');
    if (mark) {
      mark.hidden = isCa;
      mark.setAttribute('aria-hidden', isCa ? 'true' : 'false');
    }
  }

  static initCountryProvinceConsultant() {
    if (typeof Shopify === 'undefined' || !Shopify.CountryProvinceSelector) return;
    const $ = JoinConsultantRegisterFormComponent.byId;
    const country = $('consultant-country');
    const province = $('consultant-province');
    if (!country || !province) return;
    if (country.dataset.joinConsultantProvinceInit === 'true') return;

    const opts = country.options;
    const tryVals = [];
    let wc = '';
    try {
      wc = (globalThis.localStorage.getItem('watkins_country') || '').trim().toUpperCase();
    } catch {
      wc = '';
    }
    if (wc === 'US' || wc === 'USA') {
      tryVals.push('United States', 'US');
    } else if (wc === 'CA' || wc === 'CAN') {
      tryVals.push('Canada', 'CA');
    }
    if (tryVals.length === 0) {
      const defIso = (country.getAttribute('data-default') || '').trim().toUpperCase();
      if (defIso === 'US' || defIso === 'USA') {
        tryVals.push('United States', 'US');
      } else if (defIso === 'CA' || defIso === 'CAN') {
        tryVals.push('Canada', 'CA');
      } else if (defIso.length === 2) {
        tryVals.push(defIso);
      }
    }

    let idx = -1;
    for (let v = 0; v < tryVals.length; v++) {
      const wanted = tryVals[v];
      for (let i = 0; i < opts.length; i++) {
        if (opts[i].value === wanted) {
          idx = i;
          break;
        }
      }
      if (idx >= 0) break;
    }
    for (let v = 0; idx < 0 && v < tryVals.length; v++) {
      const wanted = tryVals[v];
      for (let i = 0; i < opts.length; i++) {
        if ((opts[i].textContent || '').trim() === wanted) {
          idx = i;
          break;
        }
      }
    }
    if (idx < 0 && typeof Shopify.setSelectorByValue === 'function') {
      Shopify.setSelectorByValue(country, country.getAttribute('data-default') || '');
      idx = country.selectedIndex;
    }
    if (idx >= 0) {
      country.selectedIndex = idx;
      const resolved = country.options[idx].value;
      country.value = resolved;
      country.dataset.default = resolved;
    } else if (country.selectedIndex >= 0) {
      country.dataset.default = country.options[country.selectedIndex].value;
    } else {
      country.dataset.default = '';
    }

    country.dataset.joinConsultantProvinceInit = 'true';
    province.dataset.default = province.dataset.default || '';
    if (country.selectedIndex >= 0) {
      for (let i = country.options.length - 1; i >= 0; i--) {
        if (!country.options[i].selected) {
          country.remove(i);
        }
      }
      country.dataset.default = country.value;
    }
    // eslint-disable-next-line no-new
    new Shopify.CountryProvinceSelector('consultant-country', 'consultant-province', {
      hideElement: 'CustomerAccountsProvinceContainer',
    });
    JoinConsultantRegisterFormComponent.syncTaxIdRequiredByMarket();
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'error'|'success'} type
   */
  static scrollFeedbackIntoView(form, type) {
    const sel = type === 'error' ? '[data-join-consultant-api-error]' : '[data-join-consultant-api-success]';
    const el = form.querySelector(sel);
    if (el && !el.classList.contains('d-none')) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'error'|'success'} type
   * @param {string} message
   * @param {{ html?: boolean }} [options] - when `html: true`, render message as HTML (API `errorHtml`)
   */
  static showFeedback(form, type, message, options) {
    const asHtml = Boolean(options && options.html);
    const errEl = form.querySelector('[data-join-consultant-api-error]');
    const okEl = form.querySelector('[data-join-consultant-api-success]');
    if (errEl) {
      errEl.classList.toggle('d-none', type !== 'error');
      if (type === 'error' && message) {
        if (asHtml) {
          errEl.innerHTML = message;
        } else {
          errEl.textContent = message;
        }
        errEl.setAttribute('tabindex', '-1');
        errEl.focus({ preventScroll: true });
      } else {
        errEl.textContent = '';
      }
    }
    if (okEl) {
      okEl.classList.toggle('d-none', type !== 'success');
      okEl.textContent = type === 'success' ? message : '';
    }
    if (type === 'error' || type === 'success') {
      JoinConsultantRegisterFormComponent.scrollFeedbackIntoView(form, type);
    }
  }

  /**
   * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
   * @returns {string}
   */
  static joinConsultantFieldValidationMessage(field) {
    const C = JoinConsultantRegisterFormComponent;
    if (field.validity.valueMissing) {
      return C.str('fieldRequired', 'Please fill out this field.');
    }
    if (field.validity.typeMismatch && field.type === 'email') {
      return C.str('emailInvalid', 'Please enter a valid email address.');
    }
    if (field.validity.patternMismatch) {
      return (
        field.validationMessage ||
        C.str('phoneInvalid', 'Enter phone as 202 4561437 (area code, space, then 7 digits).')
      );
    }
    return field.validationMessage || C.str('fieldRequired', 'Please fill out this field.');
  }

  /**
   * @param {HTMLElement} form
   */
  static clearJoinConsultantFieldErrors(form) {
    if (!form) return;
    form.querySelectorAll('[data-join-consultant-field-error]').forEach(function (el) {
      el.remove();
    });
    form.querySelectorAll('.is-invalid').forEach(function (el) {
      el.classList.remove('is-invalid');
      el.removeAttribute('aria-invalid');
    });
  }

  /**
   * @param {HTMLElement} control
   * @returns {HTMLElement|null}
   */
  static fieldErrorAnchor(control) {
    if (!control) return null;
    if (control.type === 'radio' || control.type === 'checkbox') {
      const fieldset = control.closest('fieldset');
      if (fieldset) return fieldset;
      return control.closest('.form-check') || control.parentElement;
    }
    const normalForm = control.closest('.normal-form');
    if (normalForm) return normalForm;
    return control.closest('.form-group') || control.parentElement;
  }

  /**
   * @param {HTMLElement} control
   */
  static clearJoinConsultantFieldError(control) {
    if (!control) return;
    control.classList.remove('is-invalid');
    control.removeAttribute('aria-invalid');
    const anchor = JoinConsultantRegisterFormComponent.fieldErrorAnchor(control);
    if (!anchor) return;
    const err = anchor.querySelector('[data-join-consultant-field-error]');
    if (err) err.remove();
  }

  /**
   * @param {HTMLElement} control
   * @param {string} message
   */
  static showJoinConsultantFieldError(control, message) {
    if (!control || !message) return;
    const C = JoinConsultantRegisterFormComponent;
    const anchor = C.fieldErrorAnchor(control);
    if (!anchor) return;
    if (anchor.querySelector('[data-join-consultant-field-error]')) return;

    control.classList.add('is-invalid');
    control.setAttribute('aria-invalid', 'true');

    const errId =
      'join-consultant-error-' + (control.id || String(control.name || 'field').replace(/\W+/g, '-'));
    const err = document.createElement('p');
    err.id = errId;
    err.className = 'join-consultant-field-error text-danger small mb-0 mt-1';
    err.setAttribute('data-join-consultant-field-error', '');
    err.setAttribute('role', 'alert');
    err.textContent = message;

    if (control.type === 'radio' || control.type === 'checkbox') {
      const formCheck = control.closest('.form-check');
      if (formCheck) {
        formCheck.insertAdjacentElement('afterend', err);
      } else {
        anchor.appendChild(err);
      }
    } else {
      control.insertAdjacentElement('afterend', err);
    }

    const describedBy = control.getAttribute('aria-describedby');
    if (describedBy && describedBy.indexOf(errId) === -1) {
      control.setAttribute('aria-describedby', describedBy + ' ' + errId);
    } else if (!describedBy) {
      control.setAttribute('aria-describedby', errId);
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  static bindJoinConsultantFieldErrorClear(form) {
    if (!form || form.__watkinsJoinConsultantFieldErrorBound) return;
    form.__watkinsJoinConsultantFieldErrorBound = true;
    const C = JoinConsultantRegisterFormComponent;
    const onFieldChange = function (e) {
      const target = e.target;
      if (!target || !target.matches || !target.matches('input, select, textarea')) return;
      C.clearJoinConsultantFieldError(target);
      if (target.type === 'radio' && target.name) {
        form.querySelectorAll('input[name="' + CSS.escape(target.name) + '"]').forEach(function (radio) {
          C.clearJoinConsultantFieldError(radio);
        });
      }
    };
    form.addEventListener('input', onFieldChange);
    form.addEventListener('change', onFieldChange);
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLElement|null} first invalid control
   */
  static validateJoinConsultantForm(form) {
    const C = JoinConsultantRegisterFormComponent;
    const $ = C.byId;
    C.clearJoinConsultantFieldErrors(form);

    let firstInvalid = null;
    const markError = function (control, message) {
      if (!control || !message) return;
      const anchor = C.fieldErrorAnchor(control);
      if (anchor && anchor.querySelector('[data-join-consultant-field-error]')) return;
      C.showJoinConsultantFieldError(control, message);
      if (!firstInvalid) firstInvalid = control;
    };

    const p1 = $('customer-accounts-password');
    const p2 = $('customer-accounts-password-confirm');
    const termsYes = $('customer-accounts-terms-yes');
    const termsNo = $('customer-accounts-terms-no');
    const hasSponsor = $('customer-accounts-has-consultant');
    const sponsorField = $('consultant-consultanatId');
    const phoneEl = $('consultant-phone');

    form.querySelectorAll('input, select, textarea').forEach(function (field) {
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) {
        field.setCustomValidity('');
      }
    });

    if (phoneEl) {
      const phoneVal = phoneEl.value.trim();
      if (phoneVal && !C.isValidConsultantPhone(phoneVal)) {
        phoneEl.setCustomValidity(
          C.str('phoneInvalid', 'Enter phone as 202 4561437 (area code, space, then 7 digits).')
        );
      }
    }

    if (p1) {
      if (!p1.value.trim()) {
        markError(p1, C.str('passwordRequired', 'Please enter a password.'));
      } else if (p1.value.length < 6) {
        markError(p1, C.str('passwordMin', 'Password must be at least 6 characters.'));
      }
    }

    if (p1 && p2 && p1.value !== p2.value) {
      markError(p2, C.str('passwordMismatch', 'Passwords do not match.'));
    }

    const email1 = $('consultant-email');
    const email2 = $('consultant-email-confirm');
    if (email1 && email2) {
      const emailValue = String(email1.value || '').trim();
      const emailConfirmValue = String(email2.value || '').trim();
      if (emailValue && emailConfirmValue && emailValue.toLowerCase() !== emailConfirmValue.toLowerCase()) {
        markError(email2, C.str('emailMismatch', 'Email addresses do not match.'));
      }
    }

    if (hasSponsor && hasSponsor.checked && sponsorField && !sponsorField.value.trim()) {
      markError(sponsorField, C.str('consultantRequired', 'Please enter your Sponsor Number.'));
    }

    if (!termsYes || !termsYes.checked) {
      markError(
        termsYes || termsNo,
        C.str('termsRequired', 'You must select Yes to agree to the terms and conditions.')
      );
    }

    form.querySelectorAll('input, select, textarea').forEach(function (field) {
      if (
        !(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)
      ) {
        return;
      }
      if (!field.willValidate || field.disabled) return;
      if (!field.checkValidity()) {
        markError(field, C.joinConsultantFieldValidationMessage(field));
      }
    });

    return firstInvalid;
  }

  /**
   * @param {HTMLFormElement} form
   */
  static clearFeedback(form) {
    const errEl = form.querySelector('[data-join-consultant-api-error]');
    const okEl = form.querySelector('[data-join-consultant-api-success]');
    if (errEl) {
      errEl.classList.add('d-none');
      errEl.textContent = '';
    }
    if (okEl) {
      okEl.classList.add('d-none');
      okEl.textContent = '';
    }
  }

  /**
   * Reads `consultantCode` from POST /apps/public/consultants JSON (top-level, nested `consultant`, or `data` / `result` / `payload` / `body`).
   * @param {unknown} data
   * @returns {string}
   */
  static extractConsultantCodeFromConsultantsApiResponse(data) {
    if (data == null || typeof data !== 'object') {
      return '';
    }
    const tryFrom = function (o) {
      if (o == null || typeof o !== 'object') {
        return '';
      }
      const nested =
        o.consultant != null && typeof o.consultant === 'object' && !Array.isArray(o.consultant)
          ? o.consultant
          : o.Consultant != null && typeof o.Consultant === 'object' && !Array.isArray(o.Consultant)
            ? o.Consultant
            : null;
      const flat = nested ? Object.assign({}, o, nested) : o;
      let code =
        flat.consultantCode != null && String(flat.consultantCode).trim() !== ''
          ? String(flat.consultantCode).trim()
          : flat.consultantcode != null && String(flat.consultantcode).trim() !== ''
            ? String(flat.consultantcode).trim()
            : flat.consultant_code != null && String(flat.consultant_code).trim() !== ''
              ? String(flat.consultant_code).trim()
              : typeof flat.code === 'string' && flat.code.trim() !== ''
                ? flat.code.trim()
                : '';
      if (code) {
        code = code.replace(/^#+/, '');
      }
      return code;
    };

    let out = tryFrom(data);
    if (out) {
      return out;
    }
    const innerKeys = ['data', 'result', 'payload', 'body'];
    for (let i = 0; i < innerKeys.length; i++) {
      const inner = /** @type {Record<string, unknown>} */ (data)[innerKeys[i]];
      out = tryFrom(inner);
      if (out) {
        return out;
      }
    }
    return '';
  }

  /**
   * Sets `#consultant-consultanatId` to the consultant code returned by the registration API.
   * @param {string} code
   */
  static applyConsultantCodeToConsultantNumberInput(code) {
    const s = String(code || '')
      .trim()
      .replace(/^#+/, '');
    if (!s) {
      return;
    }
    const el = JoinConsultantRegisterFormComponent.byId('consultant-consultanatId');
    if (!el || !(el instanceof HTMLInputElement)) {
      return;
    }
    el.value = s;
    el.setCustomValidity('');
  }

  static cartAddJsUrl() {
    const r = typeof Shopify !== 'undefined' && Shopify.routes ? Shopify.routes : null;
    if (r && r.cart_add_url) {
      const u = String(r.cart_add_url).split('?')[0];
      return u.indexOf('.js') !== -1 ? u : u.replace(/\/?$/, '') + '.js';
    }
    return '/cart/add.js';
  }

  /**
   * Same shape as check-consultant.js cart update URL (theme exposes globalThis.routes.cart_update_url).
   * @returns {string}
   */
  static cartUpdateJsUrl() {
    const r = typeof globalThis !== 'undefined' && globalThis.routes ? globalThis.routes : null;
    let u = (r && r.cart_update_url) || '/cart/update.js';
    if (u && !/\.js(\?|$)/.test(u) && /\/cart\/update$/i.test(String(u).split('?')[0])) {
      u = u.indexOf('?') === -1 ? u + '.js' : u.replace('?', '.js?');
    }
    return u;
  }

  /**
   * @param {unknown} raw
   * @returns {string}
   */
  static sanitizeConsultantIdForCartAttribute(raw) {
    if (raw == null) return '';
    const id = String(raw).trim();
    if (!id || id.length > 64) return '';
    if (!/^[\w.-]+$/i.test(id)) return '';
    return id;
  }

  /**
   * Sets cart note attributes.consultantId (matches check-consultant ATTR_CART_CONSULTANT_ID).
   * @param {string} consultantIdRaw — value from #consultant-consultanatId
   * @returns {Promise<boolean>} true if skipped (empty/invalid) or request ok
   */
  static setCartConsultantIdAttribute(consultantIdRaw) {
    const id = JoinConsultantRegisterFormComponent.sanitizeConsultantIdForCartAttribute(consultantIdRaw);
    if (!id) {
      return Promise.resolve(true);
    }
    return fetch(JoinConsultantRegisterFormComponent.cartUpdateJsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { consultantId: id } }),
    })
      .then(function (r) {
        return r.ok;
      })
      .catch(function () {
        return false;
      });
  }

  static checkoutUrl() {
    if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
      const root = String(Shopify.routes.root);
      if (root === '/' || root === '') return '/checkout';
      return (root.endsWith('/') ? root : root + '/') + 'checkout';
    }
    return '/checkout';
  }

  /**
   * Membership product from theme + user selected the Watkins membership option on the form.
   * @returns {boolean}
   */
  static shouldAddMembershipAndGoToCheckout() {
    const $ = JoinConsultantRegisterFormComponent.byId;
    const mem = window.__watkinsJoinConsultantMembership;
    const radio = $('consultant-Watkins-membership');
    if (!mem || !mem.variantId || !radio || !radio.checked) return false;
    if (mem.requiresSellingPlan && !mem.sellingPlanId) return false;
    return true;
  }

  /**
   * Clear cart, add membership via permalinks, then checkout.
   * Chain: `/cart/clear` → `/cart/add` (variant + optional `attributes[consultantId]`) → `/checkout`
   * @returns {Promise<boolean>} true if navigation to checkout was triggered
   */
  static addMembershipLineAndGoCheckout() {
    const $ = JoinConsultantRegisterFormComponent.byId;
    const sponsorEl = $('consultant-consultanatId');
    const sponsorConsultantId = sponsorEl ? sponsorEl.value : '';
    const result = JoinConsultantRegisterFormComponent.getMembershipCheckoutPermalinkUrl({
      sponsorConsultantId: sponsorConsultantId,
      navigate: true,
    });
    return Promise.resolve(result.ok);
  }

  static themeRoutes() {
    return (
      (typeof globalThis !== 'undefined' && globalThis.routes ? globalThis.routes : null) ||
      (typeof Shopify !== 'undefined' && Shopify.routes ? Shopify.routes : null)
    );
  }

  /**
   * @returns {string}
   */
  static cartAddPermalinkPath() {
    const r = JoinConsultantRegisterFormComponent.themeRoutes();
    if (r && r.cart_add_url) {
      return String(r.cart_add_url).split('?')[0].replace(/\.js$/i, '');
    }
    return '/cart/add';
  }

  /**
   * @returns {string}
   */
  static cartClearPermalinkPath() {
    const addPath = JoinConsultantRegisterFormComponent.cartAddPermalinkPath();
    if (addPath.indexOf('/cart/add') !== -1) {
      return addPath.replace('/cart/add', '/cart/clear');
    }
    return '/cart/clear';
  }

  /**
   * @returns {string}
   */
  static checkoutPermalinkPath() {
    const r = JoinConsultantRegisterFormComponent.themeRoutes();
    const root = r && r.root_url ? String(r.root_url) : '';
    if (!root || root === '/') {
      return '/checkout';
    }
    return (root.endsWith('/') ? root.slice(0, -1) : root) + '/checkout';
  }

  /**
   * `/cart/add` permalink for membership variant (quantity 1) and optional sponsor attribute.
   * Points/Bonus come from the membership variant's `points` / `bonus_value` / `cad_bonus_value`
   * metafields (see window.__watkinsJoinConsultantMembership) and use the same property names
   * (Utility.getConsultantPointsPropertyName / getConsultantBonusPropertyName) and HBB
   * Associates-vs-Customers prefix rule as every other add-to-cart line in the theme, so
   * membership behaves identically to a regular product's consultant Points/Bonus.
   * @param {{ variantId: string|number, sellingPlanId?: string|number|null, requiresSellingPlan?: boolean, points?: string, bonusUs?: string, bonusCa?: string }} mem
   * @param {string} sponsorConsultantIdRaw
   * @returns {string}
   */
  static buildMembershipCartAddPermalink(mem, sponsorConsultantIdRaw) {
    const params = new URLSearchParams();
    params.set('id', String(mem.variantId));
    params.set('quantity', '1');
    if (mem.sellingPlanId != null && mem.sellingPlanId !== '') {
      params.set('selling_plan', String(mem.sellingPlanId));
    }
    const consultantId = JoinConsultantRegisterFormComponent.sanitizeConsultantIdForCartAttribute(
      sponsorConsultantIdRaw
    );
    if (consultantId) {
      params.set('attributes[consultantId]', consultantId);
    }

    const points = String(mem.points || '').trim();
    if (points) {
      params.set(Utility.getConsultantPointsPropertyName(), points);
    }
    const bonus = String(
      Utility.resolveConsultantBonus({ bonusUs: mem.bonusUs, bonusCa: mem.bonusCa }) || ''
    ).trim();
    if (bonus) {
      params.set(Utility.getConsultantBonusPropertyName(), bonus);
    }

    params.set('return_to', JoinConsultantRegisterFormComponent.checkoutPermalinkPath());
    return JoinConsultantRegisterFormComponent.cartAddPermalinkPath() + '?' + params.toString();
  }

  /**
   * `/cart/clear` permalink chaining to membership add so the cart holds only that line.
   * @param {{ variantId: string|number, sellingPlanId?: string|number|null, requiresSellingPlan?: boolean }} mem
   * @param {string} sponsorConsultantIdRaw
   * @returns {string}
   */
  static buildMembershipClearAndAddPermalink(mem, sponsorConsultantIdRaw) {
    const addPath = JoinConsultantRegisterFormComponent.buildMembershipCartAddPermalink(mem, sponsorConsultantIdRaw);
    return (
      JoinConsultantRegisterFormComponent.cartClearPermalinkPath() + '?return_to=' + encodeURIComponent(addPath)
    );
  }

  /**
   * Build membership checkout permalink (clear → add → checkout) for testing.
   * @param {{ sponsorConsultantId?: string, navigate?: boolean }|string} [opts]
   * @returns {{ ok: boolean, url: string, reason?: string }}
   */
  static getMembershipCheckoutPermalinkUrl(opts) {
    const C = JoinConsultantRegisterFormComponent;
    let navigate = false;
    let sponsorConsultantId = '';

    if (typeof opts === 'string') {
      sponsorConsultantId = opts;
    } else if (opts && typeof opts === 'object') {
      if (opts.sponsorConsultantId != null) {
        sponsorConsultantId = String(opts.sponsorConsultantId);
      }
      if (opts.navigate === true) {
        navigate = true;
      }
    }

    if (!sponsorConsultantId) {
      const sponsorEl = C.byId('consultant-consultanatId');
      sponsorConsultantId = sponsorEl ? sponsorEl.value : '';
    }

    const mem = window.__watkinsJoinConsultantMembership;
    if (!mem || !mem.variantId) {
      return {
        ok: false,
        url: '',
        reason: 'Membership product not configured (window.__watkinsJoinConsultantMembership).',
      };
    }
    if (mem.requiresSellingPlan && !mem.sellingPlanId) {
      return {
        ok: false,
        url: '',
        reason: 'Membership requires a selling plan but none is configured.',
      };
    }

    const url = C.buildMembershipClearAndAddPermalink(mem, sponsorConsultantId);
    if (navigate) {
      window.location.href = url;
    }
    return { ok: true, url: url };
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement|null} submitBtn
   * @param {{ membershipCartFailed?: boolean, consultantCodeFromApi?: string }|null} [opts]
   */
  static finishJoinConsultantSuccessUi(form, submitBtn, opts) {
    const C = JoinConsultantRegisterFormComponent;
    let msg = C.str('apiSuccess', 'Registration submitted successfully. Thank you!');
    if (opts && opts.membershipCartFailed) {
      msg = msg + ' ' + C.str('joinConsultantMembershipCartError', 'Could not add membership to cart.');
    }
    C.showFeedback(form, 'success', msg);
    form.reset();
    if (typeof globalThis.__watkinsApplyJoinConsultantPrefill === 'function') {
      globalThis.__watkinsApplyJoinConsultantPrefill();
    }
    C.syncConsultantSponsorShell();
    C.initCountryProvinceConsultant();
    C.syncTaxIdRequiredByMarket();
    if (opts && opts.consultantCodeFromApi) {
      C.applyConsultantCodeToConsultantNumberInput(opts.consultantCodeFromApi);
    }
    Utility.setSubmitButtonLoading(submitBtn, false);
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Record<string, unknown>}
   */
  static buildPayload(form) {
    const C = JoinConsultantRegisterFormComponent;
    const $ = C.byId;
    const c = C.cfg();
    const email = ($('consultant-email') && $('consultant-email').value.trim()) || '';
    const line1 = ($('consultant-address') && $('consultant-address').value.trim()) || '';
    const line2 = ($('consultant-address-2') && $('consultant-address-2').value.trim()) || '';
    const addressLine1 = line1 + (line2 ? ' ' + line2 : '');

    const hasSponsor = $('customer-accounts-has-consultant') && $('customer-accounts-has-consultant').checked;
    const sponsorRaw = ($('consultant-consultanatId') && $('consultant-consultanatId').value.trim()) || '';

    const countryEl = $('consultant-country');
    const provinceEl = $('consultant-province');
    const addressCountry = countryEl ? countryEl.value : '';
    const stateProvince = provinceEl ? provinceEl.value : '';

    return {
      email: email,
      firstName: ($('consultant-firstname') && $('consultant-firstname').value.trim()) || '',
      lastName: ($('consultant-lastname') && $('consultant-lastname').value.trim()) || '',
      level: c.level,
      sponsorId: hasSponsor ? sponsorRaw : '',
      consultantId: C.consultantIdFromEmail(email),
      password: ($('customer-accounts-password') && $('customer-accounts-password').value) || '',
      legalName: ($('consultant-accounts-corporate') && $('consultant-accounts-corporate').value.trim()) || '',
      phone: C.digitsPhone($('consultant-phone') && $('consultant-phone').value),
      addressLine1: addressLine1,
      city: ($('consultant-city') && $('consultant-city').value.trim()) || '',
      addressCountry: addressCountry,
      stateProvince: stateProvince,
      postalCode: ($('consultant-zip') && $('consultant-zip').value.trim()) || '',
      taxId: ($('consultant-taxId') && $('consultant-taxId').value.trim()) || '',
    };
  }

  static bindFormsAndGlobals() {
    const C = JoinConsultantRegisterFormComponent;
    const forms = document.querySelectorAll('[data-join-consultant-form]');
    if (!forms.length) {
      return;
    }

    forms.forEach(function (form) {
      if (form.__watkinsJoinConsultantBound) {
        return;
      }
      form.__watkinsJoinConsultantBound = true;
      new JoinConsultantRegisterFormBinding(form).attach();
    });

    const toggle = C.byId('customer-accounts-has-consultant');
    if (toggle && toggle.dataset.joinConsultantSponsorSyncBound !== 'true') {
      toggle.dataset.joinConsultantSponsorSyncBound = 'true';
      toggle.addEventListener('change', C.syncConsultantSponsorShell);
    }
    globalThis.__watkinsSyncJoinConsultantSponsorShell = C.syncConsultantSponsorShell;
    C.syncConsultantSponsorShell();
    C.initCountryProvinceConsultant();
    C.initConsultantPhoneField();
    C.syncTaxIdRequiredByMarket();
    if (document.documentElement.dataset.joinConsultantTaxIdMarketBound !== 'true') {
      document.documentElement.dataset.joinConsultantTaxIdMarketBound = 'true';
      document.addEventListener('watkins:watkins-country-updated', C.syncTaxIdRequiredByMarket);
      globalThis.addEventListener('storage', function (e) {
        if (e.key === 'watkins_country') C.syncTaxIdRequiredByMarket();
      });
    }
  }

  static initAfterDom() {
    JoinConsultantRegisterFormComponent.loadConfigFromThemeJson().then(function () {
      JoinConsultantRegisterFormComponent.bindFormsAndGlobals();
    });
  }

  static init() {
    if (window.__watkinsJoinConsultantRegisterLoaded) {
      return;
    }
    window.__watkinsJoinConsultantRegisterLoaded = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', JoinConsultantRegisterFormComponent.initAfterDom);
    } else {
      JoinConsultantRegisterFormComponent.initAfterDom();
    }
  }
}

class JoinConsultantRegisterFormBinding {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this._onSubmit = this.handleSubmit.bind(this);
  }

  attach() {
    this.form.noValidate = true;
    JoinConsultantRegisterFormComponent.bindJoinConsultantFieldErrorClear(this.form);
    this.form.addEventListener('submit', this._onSubmit);
  }

  /**
   * Preview membership checkout URL without submitting the form.
   * Console: `__watkinsGetMembershipCheckoutUrl()` or `{ navigate: false }` to log only.
   * @param {{ sponsorConsultantId?: string, navigate?: boolean }|string} [opts]
   * @returns {{ ok: boolean, url: string, reason?: string }}
   */
  static getMembershipCheckoutUrl(opts) {
    const result = JoinConsultantRegisterFormComponent.getMembershipCheckoutPermalinkUrl(opts);
    console.log('[watkins] membership checkout URL:', result);
    return result;
  }

  /**
   * @param {SubmitEvent} e
   */
  handleSubmit(e) {
    const form = e.target;
    if (!form || form !== this.form || !form.matches || !form.matches('[data-join-consultant-form]')) {
      return;
    }

    const C = JoinConsultantRegisterFormComponent;
    const $ = C.byId;

    e.preventDefault();
    C.clearFeedback(form);

    const termsYes = $('customer-accounts-terms-yes');
    const firstInvalid = C.validateJoinConsultantForm(form);
    if (firstInvalid) {
      if (typeof firstInvalid.focus === 'function') {
        firstInvalid.focus({ preventScroll: true });
      }
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const termsHidden = $('customer-accounts-terms-hidden');
    if (termsHidden && termsYes) termsHidden.value = termsYes.checked ? 'Yes' : 'No';

    const submitBtn = form.querySelector('button[type="submit"]');
    Utility.setSubmitButtonLoading(submitBtn, true);




    const c = C.cfg();
    const payload = C.buildPayload(form);

    fetch(c.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        if (r.status === 204) {
          return { ok: r.ok, status: r.status, data: {}, jsonOk: true };
        }
        return r
          .json()
          .then(function (data) {
            return { ok: r.ok, status: r.status, data: data, jsonOk: true };
          })
          .catch(function () {
            return { ok: r.ok, status: r.status, data: {}, jsonOk: false };
          });
      })
      .then(function (result) {
        if (result.ok && result.jsonOk) {
          const consultantCode = C.extractConsultantCodeFromConsultantsApiResponse(result.data);

          if (C.shouldAddMembershipAndGoToCheckout()) {
            if (consultantCode) {
              C.applyConsultantCodeToConsultantNumberInput(consultantCode);
            }
            return C.addMembershipLineAndGoCheckout().then(function (navigated) {
              if (navigated) {
                return { navigated: true };
              }
              C.finishJoinConsultantSuccessUi(form, submitBtn, {
                membershipCartFailed: true,
                consultantCodeFromApi: consultantCode || undefined,
              });
              return { navigated: false };
            });
          }
          C.finishJoinConsultantSuccessUi(form, submitBtn, {
            consultantCodeFromApi: consultantCode || undefined,
          });
          return { navigated: false };
        }
        const data = result.data || {};
        const errorHtmlRaw = data.errorHtml || data.errorHTML || data.error_html;
        const errorHtml =
          errorHtmlRaw != null && String(errorHtmlRaw).trim()
            ? String(errorHtmlRaw).trim()
            : '';
        const errorTextRaw = data.error != null ? String(data.error).trim() : '';
        const apiMsg =
          errorHtml ||
          errorTextRaw ||
          (data.message && String(data.message).trim()) ||
          (data.detail && String(data.detail).trim()) ||
          C.str('apiError', 'Registration could not be completed. Please try again.') +
            ' (' +
            result.status +
            ')';
        C.showFeedback(form, 'error', apiMsg, { html: Boolean(errorHtml) });
        Utility.setSubmitButtonLoading(submitBtn, false);
        return { navigated: false };
      })
      .catch(function () {
        C.showFeedback(
          form,
          'error',
          C.str('networkError', 'Network error. Check your connection or try again later.')
        );
        Utility.setSubmitButtonLoading(submitBtn, false);
      });
  }
}

globalThis.JoinConsultantRegisterFormComponent = JoinConsultantRegisterFormComponent;
globalThis.__watkinsGetMembershipCheckoutUrl = function (opts) {
  return JoinConsultantRegisterFormBinding.getMembershipCheckoutUrl(opts);
};
JoinConsultantRegisterFormComponent.init();
